import { SOURCE_IDS } from '@opensentry/shared';
import { useMutation } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/Button.js';
import { useAiUsage, useCursors, useRuns, useStats } from '../hooks/useData.js';
import { api } from '../lib/api.js';
import { fmtTime } from '../lib/format.js';

export function Admin() {
  const runs = useRuns();
  const cursors = useCursors();
  const stats = useStats();
  const ai = useAiUsage();
  const trigger = useMutation({ mutationFn: (s: string) => api.triggerIngest(s) });

  return (
    <div className="space-y-6 p-6">
      <h1 className="text-lg font-semibold">Admin · ingest ops</h1>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['events', stats.data?.events.total],
          ['zones', stats.data?.zones],
          ['employees', stats.data?.employees],
          ['matches', stats.data?.matches],
        ].map(([k, v]) => (
          <div
            key={k}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3"
          >
            <div className="mono text-xl">{v ?? 0}</div>
            <div className="text-xs text-[var(--color-muted)]">{k}</div>
          </div>
        ))}
      </section>

      <section>
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-sm font-semibold">AI cost &amp; behaviour</h2>
          <span className="mono text-[11px] text-[var(--color-muted)]">
            {ai.data
              ? ai.data.enabled
                ? `${ai.data.provider}`
                : 'disabled — set ANTHROPIC_API_KEY'
              : '…'}
          </span>
        </div>
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ['total calls', ai.data?.totals.calls ?? 0],
            ['spend', `$${(ai.data?.totals.costUsd ?? 0).toFixed(4)}`],
            ['cache hit', `${Math.round((ai.data?.totals.cacheHitRate ?? 0) * 100)}%`],
            ['latency p50/p95', `${ai.data?.latencyMs.p50 ?? 0}/${ai.data?.latencyMs.p95 ?? 0}ms`],
          ].map(([k, v]) => (
            <div
              key={k as string}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-2)] p-3"
            >
              <div className="mono text-lg">{v}</div>
              <div className="text-xs text-[var(--color-muted)]">{k}</div>
            </div>
          ))}
        </div>
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-xs">
            <thead className="text-left text-[var(--color-muted)]">
              <tr>
                <th className="px-3 py-2">Feature</th>
                <th className="px-3 py-2">Calls</th>
                <th className="px-3 py-2">Cost</th>
                <th className="px-3 py-2">Cache</th>
                <th className="px-3 py-2">Errors</th>
              </tr>
            </thead>
            <tbody>
              {(ai.data?.byFeature ?? []).map((f) => (
                <tr key={f.feature} className="border-t border-[var(--color-border)]">
                  <td className="px-3 py-1.5">{f.feature}</td>
                  <td className="mono px-3 py-1.5">{f.calls}</td>
                  <td className="mono px-3 py-1.5">${f.costUsd.toFixed(5)}</td>
                  <td className="mono px-3 py-1.5">{Math.round(f.cacheHitRate * 100)}%</td>
                  <td className="mono px-3 py-1.5">{Math.round(f.errorRate * 100)}%</td>
                </tr>
              ))}
              {(ai.data?.byFeature.length ?? 0) === 0 && (
                <tr>
                  <td className="px-3 py-2 text-[var(--color-muted)]" colSpan={5}>
                    No AI calls yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Sources</h2>
        <div className="flex flex-wrap gap-2">
          {SOURCE_IDS.map((s) => (
            <div
              key={s}
              className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-panel-2)] px-3 py-1.5 text-xs"
            >
              <span className="uppercase">{s}</span>
              <span
                className="mono cursor-help text-[var(--color-muted)]"
                title={
                  cursors.data?.[s]
                    ? '● source has a saved cursor — it has ingested at least once'
                    : '○ no cursor yet — source has not ingested (or needs a key/appname)'
                }
              >
                {cursors.data?.[s] ? '●' : '○'}
              </span>
              <Button
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px]"
                disabled={trigger.isPending}
                onClick={() => trigger.mutate(s)}
              >
                <RefreshCw size={11} className={trigger.isPending ? 'animate-spin' : ''} />
                Run now
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Recent runs</h2>
        <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
          <table className="w-full text-xs">
            <thead className="text-left text-[var(--color-muted)]">
              <tr>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Fetched</th>
                <th className="px-3 py-2">Inserted</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {(runs.data ?? []).map((r) => (
                <tr key={r.id} className="border-t border-[var(--color-border)]">
                  <td className="px-3 py-1.5 uppercase">{r.source}</td>
                  <td className="mono px-3 py-1.5">{fmtTime(r.startedAt)}</td>
                  <td className="px-3 py-1.5">{r.status}</td>
                  <td className="mono px-3 py-1.5">{r.eventsFetched}</td>
                  <td className="mono px-3 py-1.5">{r.eventsInserted}</td>
                  <td className="px-3 py-1.5 text-[var(--color-sev-critical)]">{r.error ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
